import { Component, Input, OnDestroy, OnInit, input, inject } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { NomenclatureType } from '@app/core/models/nomenclature.model';
import { Structure } from '@app/core/models/structure.model';
import { NotificationService } from '@app/core/notifications/notification.service';
import { ObjetMaquette } from '@app/features/editeur/models/objet-maquette.model';
import { CommandFactoryService } from '@app/features/editeur/services/command-factory.service';
import { ObjetMaquetteService } from '@app/features/editeur/services/objet-maquette.service';
import { IfIsNotAuthorized } from '@app/shared/autorisations/autorisations.decorator.ts';
import { Traitement } from '@app/shared/autorisations/traitements';
import { StructureService } from '@app/shared/structure/structure.service';
import { Contexte, Espace, ObjetMaquetteSummary } from '@pcscol/odf-api-v1-angular';
import { Observable, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { DescripteursFormatEnseignementsForm } from './descripteurs-format-enseignements-form';
import { MatCardTitle, MatCardContent, MatCard, MatCardHeader } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix, MatError, MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatIcon } from '@angular/material/icon';
import { SelectMultipleStructuresComponent } from './select-multiple-structures/select-multiple-structures.component';
import { EnableIfAutoriseDirective } from '../../../../../../shared/autorisations/directives/enable-if-autorise.directive';
import { SelectNomenclatureComponent } from '../../../../../../shared/select-nomenclature/select-nomenclature/select-nomenclature.component';
import { MatIconButton, MatButton } from '@angular/material/button';
import { VolumeHoraireInputComponent } from './volume-horaire-input/volume-horaire-input.component';
import { MatTooltip } from '@angular/material/tooltip';
import { FormMatErrorsDirective } from '../../../../../../shared/base-form/form-mat-errors/form-mat-errors.directive';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-formulaire-descripteurs-format-enseignements',
    templateUrl: './descripteurs-format-enseignements.component.html',
    styleUrls: ['./descripteurs-format-enseignements.component.scss'],
    imports: [
        MatCardTitle,
        MatCardContent,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatSelect,
        MatOption,
        MatIcon,
        MatSuffix,
        SelectMultipleStructuresComponent,
        EnableIfAutoriseDirective,
        MatCard,
        MatCardHeader,
        SelectNomenclatureComponent,
        MatIconButton,
        VolumeHoraireInputComponent,
        MatTooltip,
        MatError,
        FormMatErrorsDirective,
        MatInput,
        MatButton,
        AsyncPipe
    ]
})
export class DescripteursFormatEnseignementsComponent implements OnInit, OnDestroy {
    private fb = inject(NonNullableFormBuilder);
    private commandFactoryService = inject(CommandFactoryService);
    private structureService = inject(StructureService);
    private objetsMaquetteService = inject(ObjetMaquetteService);
    private notificationService = inject(NotificationService);

    readonly ODF_MODIFIER_FORMATS_ENSEIGNEMENT_OBJET_MAQUETTE = Traitement.ODF_MODIFIER_FORMATS_ENSEIGNEMENT_OBJET_MAQUETTE;

    readonly NomenclatureType = NomenclatureType;
    readonly getNomenclatureFullValueFn = n => n?.libelleAffichage || n?.libelle;
    form!: DescripteursFormatEnseignementsForm;
    @Input() objetMaquetteSelected: ObjetMaquette;
    readonly espace = input.required<Espace>();
    formationsPorteusesPossibles$: Observable<ObjetMaquetteSummary[]>;

    private destroy$ = new Subject<void>();

    ngOnInit() {
        this.setData();
        this.disableFormIfNotAutorised();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @IfIsNotAuthorized(Traitement.ODF_MODIFIER_FORMATS_ENSEIGNEMENT_OBJET_MAQUETTE)
    disableFormIfNotAutorised() {
        this.form.disable();
    }

    private setData() {
        this.form = DescripteursFormatEnseignementsForm.createForm(
            this.fb,
            this.objetMaquetteSelected.typeObjetMaquette!,
            this.espace(),
            this.objetMaquetteSelected.formatsEnseignement
        );
        this.formationsPorteusesPossibles$ = this.objetsMaquetteService
            .lireObjetMaquettesIds([...new Set(this.objetMaquetteSelected.contextes!.map((ctx: Contexte) => ctx.chemin![0]))])
            .pipe(takeUntil(this.destroy$));
    }

    modifierChargesEnseignement() {
        this.commandFactoryService
            .modifierFormatsEnseignementsCommand(
                this.objetMaquetteSelected.id!,
                this.objetMaquetteSelected.version,
                this.objetMaquetteSelected.formatsEnseignement!,
                this.form.asFormatEnseignementsRequest
            )
            .do()
            .pipe(first(), takeUntil(this.destroy$))
            .subscribe(om => {
                this.objetMaquetteSelected = om;
                this.form.asFormGroup.markAsPristine();
                this.setData();
                this.notificationService.success(
                    `Le format des enseignements de l'objet ${this.objetMaquetteSelected.code} ont été enregistrés avec succès.`
                );
            });
    }

    lireStructure(code: string): Observable<Structure> {
        return this.structureService.lireStructureByCode(code);
    }

    formatTime(index: number, value: string): void {
        const [hours, minutes] = value.split(':');
        const duration: number = +hours * 60 + +minutes;
        this.form.getVolumeHoraireAtIndex(index).patchValue(duration);
    }
}
